// The post-booking form in the confirmation's right-hand column.
//
// Captures the ball-fitting upsell (US), the postcode, "How did you hear about
// us?" (international), and — in the OPTIONAL phone flavour — the pre-fit-call
// phone number and SMS opt-in.
//
// It used to open with a "To get the most from your fitting…" heading and a
// link to the pre-fit questionnaire. Both were removed in every market
// (September 2026): the questionnaire is delivered in the post-appointment
// email now, and the heading had outlived the button it introduced.
// International opens on the hear-about question instead, rendered as the
// column heading; the US column has no heading, because it has never carried
// that question and a heading with nothing beneath it is worse than none.
//
// Phone flavour:
//   optional (default, production) — the phone is collected HERE: a
//     "Your phone number for a pre-fit call" field + the SMS-consent opt-in.
//   mandatory — the phone + SMS opt-in were already collected on Step 1, so
//     this step drops both and keeps only the ball-fitting toggle, the "your
//     fitter will call you" note, and Submit.

import styles from '../styles/widget.module.css';
import { emit } from '../state/events';
import { submitPostBookingForm, ApiError } from '../api/client';
import type { BookingAction, BookingState } from '../state/machine';
import Checkmark from '../assets/icons/Checkmark';
import { AlertMessage } from '../components/AlertMessage';
import {
  formatPhoneFromInput,
  isPhoneComplete,
  phoneDialCodeLabel,
  phoneForApi,
  phoneInputValue,
  phonePlaceholder,
} from '../utils/phone';
import { countryConfigOrDefault, marketingConsentCombined } from '../config/countries';
import { isValidPhoneNumber } from "libphonenumber-js";

// A number carrying its own `+` country code is validated as written. A bare
// national number is validated against the market the widget was mounted for,
// which is what a customer types when entering their own number — a UK mobile
// checked against US rules fails for no reason they can see.
export const validatePhone = (value: string, country?: string | null) => {
  if (!value) return true; // optional field
  if (value.trim().startsWith('+')) return isValidPhoneNumber(value);
  return isValidPhoneNumber(value, countryConfigOrDefault(country).phone.region);
};

interface GetSetUpProps {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  /**
   * When true the phone + SMS opt-in were collected on Step 1, so they are
   * omitted here. Default false = production: phone + SMS live on this step.
   */
  phoneMandatory?: boolean;
}

/**
 * The answers CC uses in its own booking engine, in its order. Kept as a
 * literal rather than fetched: Salesforce takes this as free text, so the list
 * is a UI decision rather than a schema one.
 */
const HEAR_ABOUT_OPTIONS = ['TV', 'Word of Mouth', 'Social Media', 'Print Ad', 'Other'] as const;

/**
 * Past this many pills the row is allowed to wrap.
 *
 * The five-option markets sit on one line, which is what CC asked for. Canada
 * has nine, and nine will not share a row this narrow — held to nowrap they
 * would overflow the column rather than shorten, since each pill is
 * white-space: nowrap by design.
 */
const HEAR_ABOUT_ONE_ROW_MAX = 5;

export function GetSetUp({ state, dispatch, phoneMandatory = false }: GetSetUpProps) {
  const { ballFittingOffered, smsOptInOffered, hearAboutOffered, postalCodeOffered, postalCodeLabel } =
    countryConfigOrDefault(state.country);
  // Per-market where the market supplies its own; the shared list otherwise.
  const hearAboutOptions =
    countryConfigOrDefault(state.country).hearAboutOptions ?? HEAR_ABOUT_OPTIONS;
  // Require at least one meaningful input so an empty submit isn't mistaken
  // for a "skip". In the mandatory flavour everything needed was already
  // captured on Step 1, so Submit is always available (ball fitting is a
  // genuinely optional upsell).
  const hasAnyInput =
    state.postBookingBallFitting === true ||
    state.postBookingSmsOptIn === true ||
    state.postBookingPostalCode.trim() !== '' ||
    state.hearAbout !== null ||
    isPhoneComplete(state.phone, state.country);
  const canSubmit = (phoneMandatory || hasAnyInput) && !state.postBookingSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (!state.confirmation) return;

    dispatch({ type: 'POST_BOOKING_SUBMIT_START' });

    try {
      await submitPostBookingForm({
        bookingId: state.confirmation.id,
        // SF /PostAppointmentUpdate verifies identity by matching the
        // supplied email to the Account on file before patching anything.
        email: state.email.trim(),
        ballFittingInterest: state.postBookingBallFitting,
        // Send bare digits (no parens/spaces/hyphens) — the field is formatted
        // for display only; the API expects the unformatted number as before.
        phone: phoneForApi(state.phone, state.country),
        // AU's single Step 1 marketing box drives both opt-ins, so SMS follows
        // the email box's state there; every other market keeps its own SMS
        // control (collected on this step in the optional-phone flavour).
        smsOptIn: marketingConsentCombined(state.country)
          ? state.promoOptIn
          : state.postBookingSmsOptIn,
        // Newsletter opt-in is captured at lead capture (the "Contact me with
        // offers" checkbox) and propagated here so the post-booking write
        // persists the user's full marketing-consent set in one shot.
        newsletterOptIn: state.promoOptIn,
        // Both added to SF's PostAppointmentUpdate on 2026-08-27. Sent only
        // when the customer actually answered — an empty string would blank a
        // value already on the record, and skipping a question is not a
        // request to erase it.
        postalCode: state.postBookingPostalCode.trim() || undefined,
        hearAbout: state.hearAbout ?? undefined,
      });
      dispatch({ type: 'POST_BOOKING_SUBMIT_SUCCESS' });
      emit('cc:booking-widget:post-booking-completed', {
        bookingId: state.confirmation.id,
      });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `Couldn’t send to your fitter (${err.status}). ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Couldn’t send to your fitter right now.';
      dispatch({ type: 'POST_BOOKING_SUBMIT_ERROR', payload: msg });
    }
  };

  // The questionnaire markets used to return here with a link and no form.
  // That is no longer possible: CC wants "how did you hear about us" on this
  // screen and sent with the post-appointment update, and those are exactly
  // the markets where it is offered — UK, Canada and Australia all have a
  // questionnaire URL, and the US, which has no questionnaire, is the one
  // market where the question is not asked. Early-returning would have hidden
  // the question in every market that wants it, with no submit to send it.
  //
  // So the link now renders at the top of the form instead of replacing it.
  // That also answers the complaint that produced the early return — "not
  // clear what I am submitting" — because the submit below now has visible
  // fields above it rather than nothing at all.
  return (
    <form
      className={styles.getSetUpAdditionalFields}
      method="post"
      action="javascript:void(0)"
      autoComplete="on"
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      {/* The pre-fit block — heading, explanation and questionnaire button — is
          gone in every market. It was removed from the flow under item 7 of the
          Australian review (the questionnaire is delivered in the
          post-appointment email instead), and the heading and copy had outlived
          the button they introduced.

          What is left in this column differs by market: international opens on
          "How did you hear about us?", which becomes the column heading below.
          The US has no heading here — it never had the question, and a heading
          with nothing under it is worse than none. */}

      {/* "How did you hear about us?" — international only. CC does not want it
          in front of US traffic, which is live and converting.

          It sat on the confirm step until 2026-08-27 because Salesforce had
          nowhere to put it afterwards. PostAppointmentUpdate now accepts it, so
          it lives here, on the screen CC asked for, alongside the submit that
          sends it. */}
      {hearAboutOffered && (
        <div className={styles.hearAbout} style={{ marginBottom: 16 }}>
          {/* Rendered as the column's h4 so it matches "Get Ready!" and "Your
              Appointment" — same size, same caps, same reserved height, so the
              three columns still start their body copy on one line. */}
          <h4 className={`${styles.getSetUpHeading} ${styles.hearAbout__heading}`}>
            How did you hear about us? <span>Optional</span>
          </h4>
          <div
            className={`${styles.hearAbout__options} ${
              hearAboutOptions.length > HEAR_ABOUT_ONE_ROW_MAX ? styles.hearAbout__optionsWrap : ''
            }`.trim()}
          >
            {hearAboutOptions.map((option) => (
              <button
                key={option}
                type="button"
                className={`${styles.hearAbout__option} ${
                  state.hearAbout === option ? styles.selected : ''
                }`}
                aria-pressed={state.hearAbout === option}
                data-cc-track="toggle"
                data-cc-track-id="hear_about"
                onClick={() =>
                  dispatch({
                    type: 'SET_HEAR_ABOUT',
                    payload: { hearAbout: state.hearAbout === option ? null : option },
                  })
                }
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      )}


      {/* Postcode. International only — CC does not want it in front of US
          traffic, where the column is the ball-fitting upsell, the pre-fit phone
          number and Submit, and nothing else.

          The widget has never held the customer's own postcode; the only one it
          sees is what they typed to find a studio, which says where they were
          looking rather than where they live.

          The hint matters more than the field. Asked bare, a postcode after the
          booking is done reads as data collection for its own sake; saying what
          it buys them is the difference between answering and skipping. */}
      {postalCodeOffered && (
        <div className={`${styles.field} ${styles.postalField}`} style={{ marginBottom: 16 }}>
          <label className={styles.label} htmlFor="cc-postal-code">
            {/* The market's own word for it. This was hardcoded "Postal Code",
                so the UK's move to "Post Code" reached the studio search and
                the label here still disagreed with it — CC caught it on the
                confirmation. */}
            {postalCodeLabel} <span>Optional</span>
          </label>
          <p className={styles.fieldHint}>
            We would like to optimize your appointment if a better slot is available.
          </p>
          <input
            id="cc-postal-code"
            className={styles.input}
            type="text"
            inputMode="text"
            autoComplete="postal-code"
            maxLength={12}
            value={state.postBookingPostalCode}
            onChange={(e) =>
              dispatch({
                type: 'POST_BOOKING_FIELD',
                payload: { postBookingPostalCode: e.target.value },
              })
            }
          />
        </div>
      )}

      {/* US only — the $25 ball fitting is not sold in the international
          markets, so the section is absent there rather than shown and
          unsellable. */}
      {ballFittingOffered && (
      <div className={styles.field} style={{ marginBottom: 16 }}>
        <label className={styles.label}>
          Do you want your swing data to be used to find the best ball for your game? ($25 additional)
        </label>
        <button
          type="button"
          className={`${styles.handednessButton} ${state.postBookingBallFitting ? styles.selected : ''}`}
          data-cc-track="toggle"
          data-cc-track-id="ball_fitting_interest"
          onClick={() =>
            dispatch({
              type: 'POST_BOOKING_FIELD',
              payload: { postBookingBallFitting: !state.postBookingBallFitting },
            })
          }
        >
          <span className={`${styles.buttonCheckbox} ${state.postBookingBallFitting ? styles.checked : ""}`}>
            {state.postBookingBallFitting ? <Checkmark /> : null}
          </span>
          <span>I would like a golf ball fitting</span>
        </button>
      </div>
      )}

      {/* Optional flavour collects the pre-fit-call phone + SMS opt-in here.
          Mandatory already captured both on Step 1, so they are omitted. */}
      {!phoneMandatory && (
        <>
          <div className={styles.field} style={{ marginBottom: 16 }}>
            <label className={styles.label} htmlFor="cc-phone-setup">
              Your phone number for a pre-fit call
            </label>
            {(() => {
              const dialCode = phoneDialCodeLabel(state.country);
              const field = (
                <input
                  id="cc-phone-setup"
                  name="tel"
                  className={styles.input}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={phoneInputValue(state.phone, state.country)}
                  placeholder={phonePlaceholder(state.country)}
                  onChange={(e) =>
                    dispatch({
                      type: 'SET_LEAD',
                      payload: { phone: formatPhoneFromInput(e.target.value, state.country) },
                    })
                  }
                />
              );
              return dialCode ? (
                <div className={styles.phoneGroup}>
                  <span className={styles.phoneDialCode} aria-hidden="true">{dialCode}</span>
                  {field}
                </div>
              ) : (
                field
              );
            })()}
            {state.phone.trim() !== '' && !validatePhone(state.phone, state.country) && (
              <div className={styles.error} style={{ marginTop: 6 }}>
                Please enter a valid phone number.
              </div>
            )}
          </div>

          {smsOptInOffered && (
          <div className={`${styles.field} ${styles.smsOptInWide}`} style={{ marginBottom: 20 }}>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={state.postBookingSmsOptIn}
                onChange={(e) =>
                  dispatch({
                    type: 'POST_BOOKING_FIELD',
                    payload: { postBookingSmsOptIn: e.target.checked },
                  })
                }
              />
              <span>By checking the box, you consent to receive marketing text messages from Club Champion at the number provided, including messages sent by autodialer.
                Consent is not a condition of purchase. Msg frequency varies. Msg &amp; data rates may apply.
                Reply HELP for help. STOP to opt-out. See{" "}
                <a
                  href={countryConfigOrDefault(state.country).privacyPolicyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.classicGreenLink}
                  style={{ textDecoration: 'underline' }}
                >
                  Privacy Policy
                </a>
                .
              </span>
            </label>
          </div>
          )}
        </>
      )}

      <button
        type="submit"
        className={`${styles.button} ${styles.sendToFitterButton} ${styles.stepButtonMobile}`}
        disabled={!canSubmit}
        data-cc-track="cta"
        data-cc-track-id="get_set_up_submit"
      >
        {state.postBookingSubmitting ? (
          <>
            <span className={styles.spinner} />
            Sending…
          </>
        ) : (
          'Submit'
        )}
      </button>
      {/* Below Submit, not above it (CC, Canada review). It describes what
          happens after the form is sent, so it reads as a consequence there
          rather than as another instruction to work through first. */}
      <p className={styles.getSetUpCallCopy}>
        Your fitter will call you to discuss your fitting and answer any
        questions you may have.
      </p>
      {
        state.postBookingError && (
          <AlertMessage title='Couldn’t send to your fitter' description={state.postBookingError} />
        )
      }
    </form >
  );
}
